/**
 * c: command registry — the bridge between (defun c:XXX ...) definitions and
 * the CommandDispatcher. The dispatcher imports ONLY this module (it must
 * never import the interpreter or the app bridge, to keep dependency
 * direction clean); the command runner registers itself here at module load.
 */

import { normalizeCommandName, CANONICAL_COMMANDS } from '../commands/canonicalCommands';
import type { LispValue } from './values';

export interface LispCommandEntry {
    /** Uppercase command name without the c: prefix (`MYCMD`). */
    name: string;
    fn: LispValue;
    /** Source script tag (file name) for unload. */
    source: string | null;
}

type LispCommandRunner = (name: string) => void;

const commands = new Map<string, LispCommandEntry>();
let runner: LispCommandRunner | null = null;

const NAME_RE = /^[A-Z0-9_!-]+$/;

/** True when the typed name is a built-in (canonical) command spelling. */
function isBuiltInName(name: string): boolean {
    const key = normalizeCommandName(name);
    return CANONICAL_COMMANDS.some((cmd) =>
        [...cmd.names['2d'], ...cmd.names['3d']].some((n) => normalizeCommandName(n) === key)
    );
}

/** Register a c: command definition (defun c:XXX). */
export function registerLispCommand(name: string, fn: LispValue, source: string | null): void {
    const upper = name.toUpperCase();
    if (!NAME_RE.test(upper) || upper.length === 0) return;
    if (isBuiltInName(upper)) {
        // Dispatch order: canonical names win (CommandDispatcher step 0).
        console.warn(
            `[LISP] c:${upper} shadows a built-in command name; the built-in wins (dispatch order).`
        );
    }
    commands.set(upper, { name: upper, fn, source });
}

/** Resolve a typed name to a c: command definition, if defined. */
export function resolveLispCommand(name: string): LispCommandEntry | null {
    return commands.get(name.toUpperCase()) ?? null;
}

/** All registered c: command names (autocomplete). */
export function listLispCommands(): string[] {
    return [...commands.keys()].sort();
}

/** Drop the c: commands contributed by one source script (unload). */
export function clearLispCommands(source: string): number {
    let removed = 0;
    for (const [name, entry] of commands) {
        if (entry.source === source) {
            commands.delete(name);
            removed++;
        }
    }
    return removed;
}

/** Drop every c: command (runtime reset). */
export function clearAllLispCommands(): void {
    commands.clear();
}

/** Called by the app bridge at module load so the dispatcher never imports it. */
export function setLispCommandRunner(fn: LispCommandRunner): void {
    runner = fn;
}

/** True when a runner is installed (LISP bridge bootstrapped). */
export function hasLispCommandRunner(): boolean {
    return runner !== null;
}

/** Execute a resolved c: command through the app bridge (fire-and-forget). */
export function runLispCommand(name: string): void {
    runner?.(name);
}
